import React, { useState, useEffect } from 'react';
import { Factory, ExternalLink, Download, ShieldCheck, Monitor } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { getFeatures, isDesignResultsOn } from '../utils/features';

// 지그 생성 — (b) 기공소 내부망 방식 (2026-10-19)
// ⚠️ 환자정보 경계: 기본(designResults OFF)에는 이 웹 화면에 환자명·치식 등 어떤 환자정보도 넣지 않는다.
//    지그 결과 목록·다운로드는 기공소 PC 로컬 화면(http://127.0.0.1:8787, 덴클 로그인 게이트)에서만 본다.
//    환자 자료는 기공소 안에만 머문다(클라우드로 올리지 않음).
//    designResults ON(완성 디자인 STL 웹 보관, 법적확정안 2026-10-20) 시에만 안내 문구를 웹 다운로드용으로 교체한다.
//    플래그 OFF 기본 → 화면 변화 0. 서버·rules·처리방침 게이트는 메인/법적보안이 담당(이 화면은 문구만).
const LOCAL_VIEWER_URL = 'http://127.0.0.1:8787';
const AGENT_WINDOWS_URL = 'https://firebasestorage.googleapis.com/v0/b/dental-lab-system.firebasestorage.app/o/agents%2FDentClusterAgent-Setup-0.9.50.exe?alt=media';

function JigLocal() {
    // 완성 디자인 STL 웹 보관 플래그 — null(미로딩)/false=현행 로컬 전용 문구, true=웹 다운로드 안내 문구
    const [designResultsOn, setDesignResultsOn] = useState(false);
    useEffect(() => {
        let alive = true;
        getFeatures()
            .then((f) => { if (alive) setDesignResultsOn(isDesignResultsOn(f)); })
            .catch((e) => { console.error('[지그 생성] 기능 스위치 조회 실패(현행 문구 유지):', e?.message || e); });
        return () => { alive = false; };
    }, []);

    return (
        <div>
            <PageHeader
                icon={Factory}
                title={'지그 생성'}
                subtitle={designResultsOn
                    ? '지그는 기공소 PC(맥)에서 만들어지고, 완성 디자인 STL은 덴클 웹에 보관되어 내려받을 수 있습니다.'
                    : '지그는 기공소 PC(맥)에서 만들어지고, 환자 자료는 기공소 안에만 머뭅니다.'}
            />

            <div style={styles.body}>
                {/* 환자정보 경계 안내 */}
                <div style={styles.noteCard}>
                    <ShieldCheck size={18} color="#0f766e" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                        {designResultsOn ? (
                            <>
                                <div style={styles.noteTitle}>{'완성 디자인 STL은 덴클 웹에 보관됩니다'}</div>
                                <p style={styles.noteText}>
                                    {'완성 디자인 STL은 덴클 웹에 보관되어 다운로드할 수 있습니다. 보관 자료에는 환자 정보가 포함되며, 처리방침에 따라 보호·보존됩니다.'}
                                </p>
                            </>
                        ) : (
                            <>
                                <div style={styles.noteTitle}>{'환자 자료는 기공소 안에만 머뭅니다'}</div>
                                <p style={styles.noteText}>
                                    {'지그 결과 목록과 다운로드는 덴클 클라우드가 아니라 기공소 PC의 로컬 화면에서 봅니다. 환자 자료는 밖으로 나가지 않습니다.'}
                                </p>
                            </>
                        )}
                    </div>
                </div>

                {/* 지그 화면 열기 */}
                <div style={styles.card}>
                    <div style={styles.cardHead}>
                        <Monitor size={18} color="#6366f1" />
                        <span style={styles.cardTitle}>{'지그 화면 열기'}</span>
                    </div>
                    <p style={styles.cardText}>
                        {'기공소 PC에서 열리는 로컬 화면입니다. 덴클 로그인 후 지그 목록을 보고 파일을 내려받을 수 있습니다.'}
                    </p>
                    <a href={LOCAL_VIEWER_URL} target="_blank" rel="noopener noreferrer" style={styles.primaryBtn}>
                        <ExternalLink size={16} />
                        {'지그 화면 열기'}
                    </a>
                    <p style={styles.hint}>
                        {'주소는 '}{LOCAL_VIEWER_URL}{' 입니다. 화면이 열리지 않으면 덴클 프로그램이 켜져 있는지 확인하세요. PC 설정에 따라 포트(끝 숫자)가 다를 수 있습니다.'}
                    </p>
                </div>

                {/* 에이전트 다운로드 */}
                <div style={styles.card}>
                    <div style={styles.cardHead}>
                        <Download size={18} color="#6366f1" />
                        <span style={styles.cardTitle}>{'덴클 프로그램 다운로드 (윈도우)'}</span>
                    </div>
                    <p style={styles.cardText}>
                        {'지그 기능이 포함된 윈도우용 풀버전입니다. 기공소 PC에 설치하세요.'}
                    </p>
                    <a href={AGENT_WINDOWS_URL} style={styles.secondaryBtn}>
                        <Download size={16} />
                        {'윈도우 설치 파일 받기'}
                    </a>
                </div>

                {/* 사용 순서 */}
                <div style={styles.card}>
                    <div style={styles.cardHead}>
                        <span style={styles.cardTitle}>{'사용 순서'}</span>
                    </div>
                    <ol style={styles.steps}>
                        <li>{'덴클 프로그램에서 지그 기능을 켜고, 케이스 폴더를 지정합니다.'}</li>
                        <li>{'맥북 작업자가 지그를 만듭니다.'}</li>
                        <li>{'[지그 화면 열기]로 로컬 화면을 열어 결과를 내려받습니다.'}</li>
                    </ol>
                </div>
            </div>
        </div>
    );
}

const styles = {
    body: { padding: 20, maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 16 },
    noteCard: {
        display: 'flex', gap: 10, padding: '14px 16px', backgroundColor: '#f0fdfa',
        border: '1px solid #99f6e4', borderRadius: 10,
    },
    noteTitle: { fontSize: 14, fontWeight: 700, color: '#0f766e', marginBottom: 4 },
    noteText: { fontSize: 13, color: '#115e59', lineHeight: 1.6, margin: 0 },
    card: {
        padding: 18, backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
        borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10,
    },
    cardHead: { display: 'flex', alignItems: 'center', gap: 8 },
    cardTitle: { fontSize: 15, fontWeight: 700, color: '#1e293b' },
    cardText: { fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0 },
    primaryBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
        padding: '10px 18px', backgroundColor: '#6366f1', color: '#ffffff',
        borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none',
    },
    secondaryBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
        padding: '10px 18px', backgroundColor: '#ffffff', color: '#334155',
        border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none',
    },
    hint: { fontSize: 12, color: '#94a3b8', lineHeight: 1.6, margin: 0 },
    steps: { margin: 0, paddingLeft: 20, fontSize: 13, color: '#475569', lineHeight: 1.9 },
};

export default JigLocal;
